/**
 * BLRRD Transform — 3×3 homogeneous matrix utilities.
 *
 * Phase 4.D.transform.1 (Wave 11.1) — Rotate + Scale + Flip + Free-transform.
 *
 * All transforms in the BLRRD editor are represented as 3×3 row-major
 * homogeneous matrices stored in `Float32Array(9)`:
 *
 *     | m0 m1 m2 |     m0..m8 = [a, b, tx,
 *     | m3 m4 m5 |                  c, d, ty,
 *     | m6 m7 m8 |                  px,py, w ]
 *
 *   Point transform:
 *     x'  = m0*x + m1*y + m2
 *     y'  = m3*x + m4*y + m5
 *     w'  = m6*x + m7*y + m8
 *   Then divide by w' for projective (perspective) transforms; for the
 *   affine subset m6=m7=0, m8=1 and division is a no-op.
 *
 * References (clean-room, MIT — no GIMP/RT/dt source consulted):
 *   • Standard linear-algebra textbooks on 2D homogeneous coordinates,
 *     e.g. Hartley & Zisserman (2003), "Multiple View Geometry in
 *     Computer Vision", Chapter 2.
 *   • Foley, van Dam, Feiner, Hughes (1990), "Computer Graphics:
 *     Principles and Practice", §5.2 (2D transformations) — the
 *     row-major convention used here matches that text.
 *
 * License: MIT (BLRRD project).
 */

/**
 * Allocate a fresh identity matrix.
 * @returns {Float32Array}
 */
export function identity() {
    const m = new Float32Array(9);
    m[0] = 1; m[4] = 1; m[8] = 1;
    return m;
}

/**
 * Matrix-matrix multiply: returns a fresh `Float32Array(9)` = a · b.
 * Immutable: inputs are not mutated.
 *
 * @param {Float32Array} a
 * @param {Float32Array} b
 * @returns {Float32Array}
 */
export function multiply(a, b) {
    if (!a || !b || a.length !== 9 || b.length !== 9) {
        throw new Error('multiply: both operands must be Float32Array(9)');
    }
    const r = new Float32Array(9);
    /* Row-major 3×3 · 3×3. Manual unroll — hot path. */
    r[0] = a[0] * b[0] + a[1] * b[3] + a[2] * b[6];
    r[1] = a[0] * b[1] + a[1] * b[4] + a[2] * b[7];
    r[2] = a[0] * b[2] + a[1] * b[5] + a[2] * b[8];

    r[3] = a[3] * b[0] + a[4] * b[3] + a[5] * b[6];
    r[4] = a[3] * b[1] + a[4] * b[4] + a[5] * b[7];
    r[5] = a[3] * b[2] + a[4] * b[5] + a[5] * b[8];

    r[6] = a[6] * b[0] + a[7] * b[3] + a[8] * b[6];
    r[7] = a[6] * b[1] + a[7] * b[4] + a[8] * b[7];
    r[8] = a[6] * b[2] + a[7] * b[5] + a[8] * b[8];
    return r;
}

/**
 * Returns determinant of the 3×3 matrix.
 * @param {Float32Array} m
 * @returns {number}
 */
export function determinant(m) {
    if (!m || m.length !== 9) {
        throw new Error('determinant: matrix must be Float32Array(9)');
    }
    return (
        m[0] * (m[4] * m[8] - m[5] * m[7])
      - m[1] * (m[3] * m[8] - m[5] * m[6])
      + m[2] * (m[3] * m[7] - m[4] * m[6])
    );
}

/**
 * Returns inverse of `m`. Throws if `m` is singular (|det| < eps).
 *
 * Uses cofactor / adjugate formula — closed-form, no LU decomposition
 * needed for 3×3 (numerically stable enough for our use case where
 * the matrices we invert come from rotation/scale/flip/shear/perspective
 * generators below, all well-conditioned).
 *
 * @param {Float32Array} m
 * @param {number} [eps=1e-12] singular-matrix threshold
 * @returns {Float32Array}
 */
export function invert(m, eps = 1e-12) {
    if (!m || m.length !== 9) {
        throw new Error('invert: matrix must be Float32Array(9)');
    }
    const det = determinant(m);
    if (!Number.isFinite(det) || Math.abs(det) < eps) {
        throw new Error(`invert: matrix is singular (det=${det})`);
    }
    const inv = 1 / det;
    const r = new Float32Array(9);
    /* Cofactor matrix transposed = adjugate. */
    r[0] =  (m[4] * m[8] - m[5] * m[7]) * inv;
    r[1] = -(m[1] * m[8] - m[2] * m[7]) * inv;
    r[2] =  (m[1] * m[5] - m[2] * m[4]) * inv;

    r[3] = -(m[3] * m[8] - m[5] * m[6]) * inv;
    r[4] =  (m[0] * m[8] - m[2] * m[6]) * inv;
    r[5] = -(m[0] * m[5] - m[2] * m[3]) * inv;

    r[6] =  (m[3] * m[7] - m[4] * m[6]) * inv;
    r[7] = -(m[0] * m[7] - m[1] * m[6]) * inv;
    r[8] =  (m[0] * m[4] - m[1] * m[3]) * inv;
    return r;
}

/**
 * Returns a rotation matrix around the origin (0,0) by `angleRad`
 * radians, counter-clockwise in image coordinates (y-down).
 *
 * To rotate around an arbitrary pivot `(cx, cy)` compose:
 *   T(cx,cy) · R(θ) · T(-cx,-cy)
 *
 * @param {number} angleRad
 * @returns {Float32Array}
 */
export function rotation(angleRad) {
    const c = Math.cos(angleRad);
    const s = Math.sin(angleRad);
    const m = new Float32Array(9);
    m[0] =  c; m[1] = -s; m[2] = 0;
    m[3] =  s; m[4] =  c; m[5] = 0;
    m[6] =  0; m[7] =  0; m[8] = 1;
    return m;
}

/**
 * Returns a non-uniform scale matrix anchored at the origin.
 * `sx, sy` may be negative (flip-equivalent), but for explicit
 * geometric flips use `flipH` / `flipV` for clarity.
 *
 * @param {number} sx
 * @param {number} sy
 * @returns {Float32Array}
 */
export function scale(sx, sy) {
    const m = new Float32Array(9);
    m[0] = sx; m[4] = sy; m[8] = 1;
    return m;
}

/**
 * Returns a translation matrix.
 *
 * @param {number} tx
 * @param {number} ty
 * @returns {Float32Array}
 */
export function translation(tx, ty) {
    const m = new Float32Array(9);
    m[0] = 1; m[4] = 1; m[8] = 1;
    m[2] = tx; m[5] = ty;
    return m;
}

/**
 * Returns a matrix that flips an image of width `w` horizontally,
 * using the **pixel-center convention** of `applyTransform` (the
 * resamplers in resample.js treat integer source coordinates as
 * pixel centres, not edges).
 *
 * Under that convention the correct mapping is `x' = (w - 1) - x`
 * (pixel 0 ↔ pixel w-1, both stay integer), NOT `w - x` (which
 * would shift everything by half a pixel and reproduce src pixels
 * via mirror-extend at the boundary). This is the fix from the
 * Codex Wave-11.1 review.
 *
 * Equivalent to: T(w-1, 0) · S(-1, 1)
 *
 * @param {number} w image width in pixels
 * @returns {Float32Array}
 */
export function flipH(w) {
    if (!Number.isFinite(w) || w < 0) {
        throw new Error(`flipH: width must be a non-negative number (got ${w})`);
    }
    const m = new Float32Array(9);
    m[0] = -1; m[2] = w - 1;
    m[4] =  1;
    m[8] =  1;
    return m;
}

/**
 * Returns a matrix that flips an image of height `h` vertically,
 * using the same pixel-center convention as `flipH` above.
 *
 * Equivalent to: T(0, h-1) · S(1, -1)
 *
 * @param {number} h image height in pixels
 * @returns {Float32Array}
 */
export function flipV(h) {
    if (!Number.isFinite(h) || h < 0) {
        throw new Error(`flipV: height must be a non-negative number (got ${h})`);
    }
    const m = new Float32Array(9);
    m[0] =  1;
    m[4] = -1; m[5] = h - 1;
    m[8] =  1;
    return m;
}

/**
 * Returns a shear matrix (origin-anchored). `shx` shears in the x
 * direction proportional to y; `shy` shears in y proportional to x.
 *
 * @param {number} shx
 * @param {number} shy
 * @returns {Float32Array}
 */
export function shear(shx, shy) {
    const m = new Float32Array(9);
    m[0] = 1; m[1] = shx;
    m[3] = shy; m[4] = 1;
    m[8] = 1;
    return m;
}

/**
 * Returns true when `m` is numerically the identity matrix.
 *
 * Used by `applyTransform` to short-circuit and return the source
 * reference unmodified — protects pixel-hash + worker-parity baselines
 * from drift when the user hasn't actually invoked a transform.
 *
 * @param {Float32Array} m
 * @param {number} [eps=1e-6]
 * @returns {boolean}
 */
export function isIdentity(m, eps = 1e-6) {
    if (!m || m.length !== 9) return false;
    return (
        Math.abs(m[0] - 1) < eps && Math.abs(m[1])     < eps && Math.abs(m[2])     < eps
     && Math.abs(m[3])     < eps && Math.abs(m[4] - 1) < eps && Math.abs(m[5])     < eps
     && Math.abs(m[6])     < eps && Math.abs(m[7])     < eps && Math.abs(m[8] - 1) < eps
    );
}

/**
 * Apply matrix `m` to a 2D point `(x, y)` and write the result into
 * `out` at `[outOffset, outOffset+1]`. Handles the projective divide
 * for perspective transforms; for affine matrices the divide is a
 * no-op (w'=1).
 *
 * @param {Float32Array} m
 * @param {number} x
 * @param {number} y
 * @param {Float32Array|number[]} out
 * @param {number} [outOffset=0]
 */
export function transformPoint(m, x, y, out, outOffset = 0) {
    const xp = m[0] * x + m[1] * y + m[2];
    const yp = m[3] * x + m[4] * y + m[5];
    const wp = m[6] * x + m[7] * y + m[8];
    if (Math.abs(wp) < 1e-30) {
        out[outOffset]     = NaN;
        out[outOffset + 1] = NaN;
        return;
    }
    const inv = 1 / wp;
    out[outOffset]     = xp * inv;
    out[outOffset + 1] = yp * inv;
}

/**
 * Compose a sequence of matrices left-to-right:
 *   compose(A, B, C) = A · B · C
 * i.e. the rightmost transform is applied first to a point.
 *
 * @param  {...Float32Array} mats
 * @returns {Float32Array}
 */
export function compose(...mats) {
    if (mats.length === 0) return identity();
    let r = mats[0];
    for (let i = 1; i < mats.length; i++) r = multiply(r, mats[i]);
    /* Always return a fresh allocation (caller may mutate). */
    if (mats.length === 1) return new Float32Array(r);
    return r;
}
